// OWNER: FRONTEND-ADMIN. Pure reading of API failures (no DOM, no fetch), so tests can import it. The server's own
// words are shown exactly as returned; the structured `details` add lines under them (go-live problems, Twilio codes,
// the SIP trunk a number is stuck on).

/** The parts of an ApiFailure (web/src/api.ts) this module reads. Duck-typed so it works without the DOM client. */
interface FailureLike { status?: unknown; body?: { error?: unknown; message?: unknown; details?: unknown } }

export interface ApiErrorView {
  status: number | null;   // HTTP status, when the error came from the API
  code: string | null;     // machine code, e.g. 'validation', 'number_on_trunk', 'twilio_error'
  message: string;         // the server's sentence, verbatim
  problems: string[];      // details.problems (go-live): every reason, one per line
  extra: string[];         // other details worth showing (Twilio error code and link, trunk SID)
}

const isFailure = (e: unknown): e is FailureLike =>
  !!e && typeof e === 'object' && 'body' in e && !!(e as FailureLike).body && typeof (e as FailureLike).body === 'object'
  && typeof (e as FailureLike).body!.message === 'string';

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x.trim()) : []);

export function apiErrorView(e: unknown): ApiErrorView {
  if (!isFailure(e)) {
    return { status: null, code: null, message: e instanceof Error ? e.message : String(e), problems: [], extra: [] };
  }
  const body = e.body!;
  const message = String(body.message);
  const d = body.details;
  const problems: string[] = [];
  const extra: string[] = [];
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const o = d as Record<string, unknown>;
    // go-live (CORE): every problem; the message only quotes the first three
    problems.push(...strings(o.problems));
    // Twilio (TELEPHONY client.ts TwilioErrorDetails)
    if (typeof o.twilioCode === 'number') extra.push(`Twilio error ${o.twilioCode}${typeof o.twilioStatus === 'number' ? ` (HTTP ${o.twilioStatus})` : ''}`);
    if (typeof o.moreInfo === 'string' && /^https:\/\//.test(o.moreInfo)) extra.push(o.moreInfo);
    if (typeof o.twilioMessage === 'string' && o.twilioMessage.trim() && !message.includes(o.twilioMessage)) extra.push(o.twilioMessage);
    // number import (409 number_on_trunk): the message names the trunk; keep the SID copyable anyway
    if (typeof o.trunkSid === 'string' && !message.includes(o.trunkSid)) extra.push(`SIP trunk ${o.trunkSid}`);
  } else if (typeof d === 'string' && d.trim() && !message.includes(d)) {
    extra.push(d);
  }
  return {
    status: typeof e.status === 'number' ? e.status : null,
    code: typeof body.error === 'string' ? body.error : null,
    message,
    // the message already quotes some problems; list them all anyway (it says "and n more")
    problems,
    extra,
  };
}
